import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/helpers";
import { SearchBar } from "./_shared";

export default function TherapyTypesTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: "", code: "", duration_min: 45, rate: 800, color: "#4A6753", icon: "Brain", is_group: false, max_group_size: 4 };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try { setItems((await api.get("/therapy-types")).data || []); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShow(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      code: t.code || "",
      duration_min: t.duration_min || 45,
      rate: t.rate ?? 0,
      color: t.color || "#4A6753",
      icon: t.icon || "Brain",
      is_group: !!t.is_group,
      max_group_size: t.max_group_size || 4,
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name || !form.code) { toast.error("Name and code required"); return; }
    try {
      if (editing) {
        await api.put(`/therapy-types/${editing.id}`, form);
        toast.success("Therapy type updated");
      } else {
        await api.post("/therapy-types", form);
        toast.success("Therapy type added");
      }
      setShow(false); setEditing(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (t) => {
    const nextActive = t.active === false;
    try {
      await api.put(`/therapy-types/${t.id}`, { active: nextActive });
      toast.success(nextActive ? "Therapy activated" : "Therapy deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = q.trim()
    ? items.filter(t => {
        const s = q.trim().toLowerCase();
        return (t.name || "").toLowerCase().includes(s)
          || (t.code || "").toLowerCase().includes(s);
      })
    : items;

  return (
    <Card>
      <CardHeader className="gap-3">
        <div className="flex flex-row justify-between items-center gap-3">
          <CardTitle className="text-base font-display tracking-tight">Therapy types ({filtered.length}{q && ` / ${items.length}`})</CardTitle>
          <Button size="sm" onClick={openNew} className="gap-1.5" data-testid="new-therapy-btn"><Plus size={14} weight="bold" />New</Button>
        </div>
        <SearchBar value={q} onChange={setQ} placeholder="Search by name or code…" testId="therapies-search" />
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {filtered.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              {q ? `No therapies match "${q}"` : "No therapy types yet."}
            </div>
          )}
          {filtered.map(t => {
            const inactive = t.active === false;
            return (
              <div key={t.id} className={`px-5 py-3 flex items-center gap-3 ${inactive ? "opacity-60" : ""}`} data-testid={`therapy-row-${t.id}`}>
                <div className="h-9 w-9 rounded-md text-white flex items-center justify-center font-mono text-xs font-bold" style={{backgroundColor: t.color}}>{t.code}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {t.name}
                    {inactive && <Badge className="ml-2 text-[10px] bg-muted text-muted-foreground border-border">Inactive</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{t.duration_min} min · {fmtMoney(t.rate)}{t.is_group && ` · Group (max ${t.max_group_size || 4})`}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-therapy-${t.id}`} className="gap-1.5"><PencilSimple size={14} /> Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => toggleActive(t)} data-testid={`toggle-therapy-${t.id}`} className="text-xs">
                  {inactive ? "Activate" : "Deactivate"}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog open={show} onOpenChange={(o) => { setShow(o); if (!o) { setEditing(null); } }}>
        <DialogContent className="bg-card max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit therapy type" : "New therapy type"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} /></div>
              <div className="space-y-1.5"><Label>Code</Label><Input value={form.code} onChange={e=>setForm({...form, code:e.target.value.toUpperCase()})} /></div>
              <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" value={form.duration_min} onChange={e=>setForm({...form, duration_min:Number(e.target.value)})} /></div>
              <div className="space-y-1.5"><Label>Rate (₹)</Label><Input type="number" value={form.rate} onChange={e=>setForm({...form, rate:Number(e.target.value)})} /></div>
              <div className="space-y-1.5"><Label>Color</Label><Input type="color" value={form.color} onChange={e=>setForm({...form, color:e.target.value})} /></div>
              <div className="space-y-1.5"><Label>Icon</Label><Input value={form.icon} onChange={e=>setForm({...form, icon:e.target.value})} /></div>
            </div>
            <label className="flex items-center gap-2"><Checkbox checked={form.is_group} onCheckedChange={v=>setForm({...form, is_group:!!v})} /><span className="text-sm">Group session</span></label>
            {form.is_group && (
              <div className="space-y-1.5 pl-6 border-l-2 border-primary/20">
                <Label>Maximum students per group session</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  className="max-w-[140px]"
                  value={form.max_group_size}
                  onChange={e=>setForm({...form, max_group_size:Number(e.target.value)})}
                  data-testid="therapy-max-group-size"
                />
                <p className="text-[11px] text-muted-foreground">
                  The Student Queue stops this many students joining one running
                  session. An owner or admin can override it when needed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="save-therapy">{editing ? "Save changes" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
